/**
 * Super admin → Families: manage every family's owner, editors, viewers and
 * phone logins. A family without an owner is marked so it can be given one.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { backend } from '../data';
import type { AdminFamily, AdminMember, Role } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { ErrorText, Field, Notice, Spinner, initials } from '../ui/components';
import { AppLoginsTab } from './AppLogins';
import { NewFamilyWizard, WelcomeWizard } from './AdminWelcome';

type P = { id: string; name: string };

async function peopleOf(familyId: string): Promise<P[]> {
  const snap = await backend.loadFamilyData(familyId);
  return snap.rows.filter(r => r.table === 'patients').sort((a, b) => a.position - b.position)
    .map(r => ({ id: r.id, name: String((r.data as { name?: string })?.name || 'Unnamed') }));
}

const ROLES: Role[] = ['owner', 'editor', 'viewer'];

export function FamiliesTab({ families, onChanged, adminName = '' }: { families: AdminFamily[]; onChanged: () => void; adminName?: string }) {
  const [sel, setSel] = useState<string>(() => (families.find(f => !f.ownerEmail) || families[0])?.familyId || '');
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [wizard, setWizard] = useState(false);

  async function createFamily(e: FormEvent) {
    e.preventDefault(); setErr(''); setMsg('');
    try {
      const id = await backend.adminCreateFamily(newName.trim(), newOwner.trim());
      setNewName(''); setNewOwner(''); setMsg(t('adm.fam.created')); onChanged(); setSel(id);
    } catch (ex) { setErr(tErr(ex)); }
  }

  const shown = families.filter(f => !filter || `${f.name} ${f.ownerEmail}`.toLowerCase().includes(filter.toLowerCase()));
  const family = families.find(f => f.familyId === sel);
  return (
    <div className="bh-admin-grid">
      <div className="bh-admin-col">
        <button className="bh-btn bh-btn-primary bh-nf-btn" onClick={() => { setWizard(true); setMsg(''); setErr(''); }}>➕ {t('nf.title')}</button>
        <div className="bh-admin-card">
          <div className="bh-row bh-users-head" style={{ justifyContent: 'space-between' }}>
            <h3 className="bh-h3">{t('adm.fam.all')} ({families.length})</h3>
            <input className="bh-search" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('adm.search')} aria-label={t('adm.search')} />
          </div>
          <div className="bh-list">
            {shown.map(f => (
              <button key={f.familyId} className={`bh-fam-row${f.familyId === sel ? ' on' : ''}`} onClick={() => { setSel(f.familyId); setMsg(''); setErr(''); }}>
                <span className="bh-strong">{f.name}</span>
                <span className="bh-small bh-muted">{f.ownerEmail ? `${t('role.owner')}: ${f.ownerEmail}` : ''}{!f.ownerEmail && <span className="bh-chip bh-chip-off">{t('adm.fam.noOwner')}</span>}</span>
                <span className="bh-small bh-muted">{t('adm.fam.counts', { people: f.patients, members: f.members })}</span>
              </button>
            ))}
          </div>
        </div>
        <details className="bh-admin-card bh-nf-existing">
        <summary className="bh-strong">{t('nf.existing')}</summary>
        <form className="bh-form" onSubmit={createFamily}>
          <p className="bh-small bh-muted">{t('nf.existingHelp')}</p>
          <Field label={t('adm.fam.name')}><input value={newName} onChange={e => setNewName(e.target.value)} required placeholder="e.g. Rahman family" /></Field>
          <Field label={t('adm.fam.ownerLogin')} hint={t('adm.fam.ownerLoginHint')}><input value={newOwner} onChange={e => setNewOwner(e.target.value)} required autoCapitalize="none" /></Field>
          <button className="bh-btn bh-btn-primary">{t('adm.fam.create')}</button>
        </form>
        </details>
      </div>
      <div className="bh-admin-card">
        {msg && <Notice tone="ok">{msg}</Notice>}
        <ErrorText>{err}</ErrorText>
        {wizard ? <NewFamilyWizard adminName={adminName} onCreated={id => { onChanged(); setSel(id); }} onClose={() => setWizard(false)} />
          : family ? <FamilyDetail key={family.familyId} family={family} onChanged={onChanged} adminName={adminName} /> : <p className="bh-muted">{t('adm.fam.pick')}</p>}
      </div>
    </div>
  );
}

function FamilyDetail({ family, onChanged, adminName }: { family: AdminFamily; onChanged: () => void; adminName: string }) {
  const [welcome, setWelcome] = useState(false);
  const fid = family.familyId;
  const [members, setMembers] = useState<AdminMember[] | null>(null);
  const [people, setPeople] = useState<P[]>([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [showLogins, setShowLogins] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([backend.adminFamilyMembers(fid), peopleOf(fid)]);
      setMembers(m); setPeople(p);
    } catch (e) { setErr(tErr(e)); setMembers([]); }
  }, [fid]);
  useEffect(() => { void reload(); }, [reload]);

  async function run(fn: () => Promise<unknown>, ok: string) {
    setErr(''); setMsg('');
    try { await fn(); await reload(); onChanged(); setMsg(ok); } catch (e) { setErr(tErr(e)); }
  }
  const nameOf = (id: string) => people.find(p => p.id === id)?.name || id;
  const hasOwner = (members || []).some(m => m.role === 'owner');

  return (
    <div>
      <h3 className="bh-h3" style={{ marginTop: 0 }}>{family.name}</h3>
      <p className="bh-small bh-muted">{t('adm.fam.people')}: {people.map(p => p.name).join(', ') || '—'}</p>
      {members && (welcome
        ? <WelcomeWizard fid={fid} familyName={family.name} people={people} hasOwner={hasOwner} adminName={adminName} onDone={() => { void reload(); onChanged(); }} onClose={() => setWelcome(false)} />
        : <button className="bh-btn bh-btn-primary" style={{ margin: '4px 0 12px' }} onClick={() => setWelcome(true)}>✉️ {t('wel.btn')}</button>)}
      {members && !hasOwner && <Notice tone="warn">{t('adm.fam.noOwnerHelp')}</Notice>}
      {msg && <Notice tone="ok">{msg}</Notice>}
      <ErrorText>{err}</ErrorText>
      {members === null ? <Spinner /> : (
        <div className="bh-list">
          {!members.length && <p className="bh-small bh-muted">{t('adm.fam.noMembers')}</p>}
          {members.map(m => (
            <MemberEditor key={m.userId} m={m} people={people} nameOf={nameOf}
              onSave={(role, all, ids) => run(() => backend.adminSetMember(fid, m.userId, role, all, ids),
                role === 'owner' && m.role !== 'owner' ? t('adm.fam.ownerMade', { name: m.displayName }) : t('fam.saved'))}
              onRemove={() => { if (confirm(t('fam.removeConfirm', { name: m.displayName || m.email }))) void run(() => backend.adminRemoveMember(fid, m.userId), t('fam.saved')); }} />
          ))}
        </div>
      )}
      <AddMember people={people} hasOwner={hasOwner} onAdd={(login, role, all, ids) => run(() => backend.adminAddMember(fid, login, role, all, ids), t('adm.fam.added'))} />
      <div style={{ marginTop: 18 }}>
        <button className="bh-btn bh-btn-ghost" onClick={() => setShowLogins(!showLogins)}>📱 {showLogins ? t('adm.fam.hideLogins') : t('adm.fam.showLogins')}</button>
        {showLogins && <AppLoginsTab fid={fid} people={people} onChanged={reload} />}
      </div>
    </div>
  );
}

function MemberEditor({ m, people, nameOf, onSave, onRemove }: {
  m: AdminMember; people: P[]; nameOf: (id: string) => string;
  onSave: (role: Role, all: boolean, ids: string[]) => void; onRemove: () => void;
}) {
  const [role, setRole] = useState<Role>(m.role);
  const [all, setAll] = useState(m.allPatients);
  const [ids, setIds] = useState<string[]>(m.patientIds);
  const changed = role !== m.role || all !== m.allPatients || ids.slice().sort().join() !== m.patientIds.slice().sort().join();
  return (
    <div className="bh-member">
      <span className="bh-acct-avatar">{initials(m.displayName || m.email)}</span>
      <div className="bh-member-main">
        <div className="bh-strong">{m.displayName} <span className={`bh-chip${m.role === 'owner' ? ' bh-chip-strong' : ''}`}>{t(`role.${m.role}`)}</span></div>
        <div className="bh-small bh-muted">{m.username ? `📱 ${t('app.username')}: ${m.username}` : m.email}</div>
        <div className="bh-small">{t('fam.access')}: {m.role === 'owner' || m.allPatients ? t('fam.access.all') : m.patientIds.map(nameOf).join(', ') || t('fam.access.none')}</div>
        <div className="bh-adm-edit">
          <select value={role} onChange={e => setRole(e.target.value as Role)} aria-label={t('fam.invite.role')}>
            {ROLES.map(r => <option key={r} value={r} disabled={m.role === 'owner' && r !== 'owner'}>{t(`role.${r}`)}</option>)}
          </select>
          {role !== 'owner' && (
            <span className="bh-adm-access">
              <label className="bh-check"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> {t('fam.access.all')}</label>
              {!all && people.map(p => (
                <label key={p.id} className="bh-check"><input type="checkbox" checked={ids.includes(p.id)} onChange={e => setIds(e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} /> {p.name}</label>
              ))}
            </span>
          )}
          {changed && <button className="bh-btn bh-btn-primary bh-btn-sm" disabled={role !== 'owner' && !all && !ids.length} onClick={() => onSave(role, role === 'owner' || all, role === 'owner' || all ? [] : ids)}>{t('common.save')}</button>}
        </div>
        {m.role === 'owner' && <div className="bh-small bh-muted">{t('adm.fam.ownerNote')}</div>}
      </div>
      {m.role !== 'owner' && <div className="bh-member-side"><button className="bh-link bh-warn" onClick={onRemove}>{t('fam.remove')}</button></div>}
    </div>
  );
}

function AddMember({ people, hasOwner, onAdd }: { people: P[]; hasOwner: boolean; onAdd: (login: string, role: Role, all: boolean, ids: string[]) => Promise<void> }) {
  const [login, setLogin] = useState('');
  const [role, setRole] = useState<Role>(hasOwner ? 'editor' : 'owner');
  const [all, setAll] = useState(true);
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => { setRole(hasOwner ? 'editor' : 'owner'); }, [hasOwner]);
  return (
    <form className="bh-form bh-app-add" onSubmit={async e => { e.preventDefault(); await onAdd(login.trim(), role, role === 'owner' || all, role === 'owner' || all ? [] : ids); setLogin(''); }}>
      <h4 style={{ margin: '0 0 6px' }}>{t('adm.fam.add')}</h4>
      <Field label={t('adm.fam.login')} hint={t('adm.fam.loginHint')}><input value={login} onChange={e => setLogin(e.target.value)} required autoCapitalize="none" /></Field>
      <Field label={t('fam.invite.role')}>
        <select value={role} onChange={e => setRole(e.target.value as Role)}>{ROLES.map(r => <option key={r} value={r}>{t(`role.${r}`)}</option>)}</select>
      </Field>
      {role !== 'owner' && (
        <Field label={t('fam.access')}>
          <div className="bh-checks">
            <label className="bh-check"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> {t('fam.access.all')}</label>
            {!all && people.map(p => <label key={p.id} className="bh-check"><input type="checkbox" checked={ids.includes(p.id)} onChange={e => setIds(e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} /> {p.name}</label>)}
          </div>
        </Field>
      )}
      <button className="bh-btn bh-btn-primary" disabled={role !== 'owner' && !all && !ids.length}>{t('adm.fam.addBtn')}</button>
    </form>
  );
}
